/**
 * GPU 合成器（M3-09 / DESIGN.md §4.4）。
 *
 * 应用层的编排入口：选后端 → 把文档译成计划（`planFromDocument`）→ 编译
 * Render Graph → 排块 → 执行 → 读回。任何一步遇到 ADR-0005 未覆盖的内容
 * 都返回 `null`，调用方（render-bridge）据此回退 CPU 合成桥。
 *
 * 上下文丢失恢复（M0-06 框架）：`ResourceRegistry` 登记纹理池与 tile 缓存，
 * `GpuContextRecovery` 把渲染调用套进 `submit()`——丢失期间的调用排队，
 * 恢复后重放；重建失败则整体判定为不可用，调用方回退 CPU（与"没有 GPU"
 * 同一条路径，不是新状态）。
 */
import type { Document } from '../../document/types';
import type { TileStore } from '../../engine/tile/store';
import type { GpuBackend } from '../../gpu/backend';
import { compileCompositePlan } from '../../gpu/graph';
import { scheduleTiles } from '../../gpu/graph/schedule';
import { executeGraph } from '../../gpu/executor/execute';
import { GpuTexturePool } from '../../gpu/executor/texture-pool';
import { selectBackend, type SelectBackendOptions } from '../../gpu/select';
import { ResourceRegistry } from '../../gpu/resource-registry';
import { GpuContextRecovery, type GpuContextState } from '../../gpu/recovery';
import { planFromDocument } from './plan';
import { UploadedTileSource } from './tile-source';

const TILE = 256;

export interface DocRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface RenderStats {
  readonly tiles: number;
  readonly draws: number;
  readonly uploads: number;
}

/**
 * 一帧的合成结果：原始 RGBA8 像素 + 尺寸。**不在这里构造 `ImageData`**——
 * 那是浏览器全局，这一层要能在 node 环境下用假后端单测（`ImageData` 在
 * node 测试环境里不存在）。调用方（render-bridge.ts，跑在浏览器里）自己
 * `new ImageData(pixels, width, height)`，与 CPU 合成桥的既有分工一致
 * （`CompositeCache` 也只给原始像素，`ImageData` 的构造在 render-bridge 里）。
 */
export interface RenderedFrame {
  readonly width: number;
  readonly height: number;
  readonly pixels: Uint8Array;
}

export class GpuCompositor {
  readonly #backend: GpuBackend;
  readonly #registry = new ResourceRegistry();
  readonly #recovery: GpuContextRecovery;
  #pool: GpuTexturePool;
  #source: UploadedTileSource | null = null;
  #sourceStore: TileStore | null = null;
  #lastStats: RenderStats | null = null;

  private constructor(backend: GpuBackend) {
    this.#backend = backend;
    this.#pool = new GpuTexturePool(backend, 'rgba8unorm');
    this.#recovery = new GpuContextRecovery({
      registry: this.#registry,
      hooks: {
        reacquire: () => this.#reacquire(),
      },
    });
  }

  /** 选后端并建一个合成器。两条后端都不可用时返回 null——调用方回退 CPU。 */
  static async create(options: SelectBackendOptions = {}): Promise<GpuCompositor | null> {
    const backend = await selectBackend(options);
    if (backend === null) return null;
    return new GpuCompositor(backend);
  }

  /**
   * 通知上下文丢失，触发恢复流程（M0-06 框架）。
   *
   * **未自动绑定浏览器事件**：`GpuBackend` 接口本身不暴露底层
   * `HTMLCanvasElement`/`GPUDevice`（backend.ts 明确不允许后端特有类型
   * 泄漏），`bindWebGLContextLoss`/`bindWebGPUDeviceLost` 需要那两个对象
   * 才能挂事件监听。这是下一轮任务要给 `GpuBackend` 加的一个窄接口
   * （比如 `onContextLost(cb)`），本任务先把恢复状态机本身接通、
   * 用这个方法供测试/未来的自动绑定调用。
   */
  notifyContextLost(detail?: string): void {
    void this.#recovery.notifyLost(detail);
  }

  /**
   * 上下文恢复后重建可复用状态。
   *
   * 纹理池与 tile 缓存持有的都是**已失效**的纹理句柄（丢失事件发生时
   * GPU 侧存储已经没了，哪怕 JS 包装对象还在）——不是"重建"它们，
   * 是整批丢弃，下次用到时按需重新创建/上传，这比对着一堆废弃句柄
   * 逐个调用 recreate() 简单且同样正确（池与 tile 缓存本来就是
   * "按需创建、可随时重建"的缓存语义，不持有需要保持身份的资源）。
   */
  async #reacquire(): Promise<boolean> {
    const result = this.#registry.recreateAll();
    if (result.failures.length > 0) return false;
    this.#pool.clear();
    // 哨兵纹理（画布底色/空白）也已失效：连同缓存一起整体重建，
    // 而不是调 invalidateAll——那只清缓存，哨兵纹理是构造时建的
    this.#source?.destroy();
    this.#source = null;
    this.#sourceStore = null;
    return true;
  }

  get backend(): GpuBackend {
    return this.#backend;
  }

  /** 上一次 `renderViewport` 的块数/draw 数/实际上传数；供验收断言与调试。 */
  get lastStats(): RenderStats | null {
    return this.#lastStats;
  }

  get recoveryState(): GpuContextState {
    return this.#recovery.state;
  }

  #sourceFor(store: TileStore): UploadedTileSource {
    if (this.#source === null || this.#sourceStore !== store) {
      this.#source?.destroy();
      this.#source = new UploadedTileSource(this.#backend, store);
      this.#sourceStore = store;
    }
    return this.#source;
  }

  /**
   * 像素改动后调用（渲染桥的 `pixelDirty`）：让对应纹理下次重新上传。
   *
   * `pixelDirty` 只给块坐标，不给图层——精确按图层失效需要渲染桥同时
   * 报告图层 id（它目前不追踪）。保守起见整份作废：结构不变只是像素变了
   * 的情况下，代价是所有已缓存的块都要重新上传一次，不是正确性问题，
   * 只是这一帧的"零上传"验收在有脏块时不成立（符合预期——那正是
   * 「干净帧零上传」这句验收标准里"干净帧"三个字的含义）。
   */
  invalidateTiles(): void {
    this.#source?.invalidateAll();
  }

  /**
   * 渲染一帧。`null` 表示文档含 ADR-0005 未覆盖的内容或渲染失败——
   * 调用方应回退 CPU 合成桥，而不是把 null 当错误处理。
   */
  async renderViewport(
    document: Document,
    tiles: TileStore,
    rect: DocRect,
    options: { readonly skipLayerId?: string; readonly signal?: AbortSignal } = {},
  ): Promise<RenderedFrame | null> {
    const plan = planFromDocument(
      document,
      options.skipLayerId === undefined ? {} : { skipLayerId: options.skipLayerId },
    );
    if (plan === null) return null;
    const signal = options.signal;

    const compiled = compileCompositePlan(plan, { linearSpace: false });
    const schedule = scheduleTiles(compiled.graph, {
      viewport: rect,
      documentSize: document.size,
      tileSize: TILE,
    });

    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));

    return this.#recovery.submit(async () => {
      const target = this.#backend.createTexture({
        width,
        height,
        format: 'rgba8unorm',
        renderTarget: true,
        label: 'gpu-compositor-viewport',
      });
      try {
        const source = this.#sourceFor(tiles);
        source.stats.uploads = 0;
        const result = executeGraph({
          backend: this.#backend,
          graph: compiled.graph,
          source,
          pool: this.#pool,
          tiles: schedule.tiles,
          tileSize: TILE,
          target,
          originX: Math.round(rect.x),
          originY: Math.round(rect.y),
          ...(signal === undefined ? {} : { signal }),
        });
        this.#lastStats = { tiles: result.tiles, draws: result.draws, uploads: source.stats.uploads };
        const pixels = await this.#backend.readback(target, signal);
        // rgba8unorm 目标的 readback 恒为 Uint8Array；ReadbackResult 的另一支
        // （Float16→Float32Array）只出现在 rgba16float 目标上，本执行器的
        // 输出目标固定 rgba8unorm，不会走到那一支
        return { width, height, pixels: pixels as Uint8Array };
      } finally {
        target.destroy();
      }
    });
  }

  destroy(): void {
    this.#source?.destroy();
    this.#pool.destroy();
    this.#registry.releaseAll();
    this.#backend.destroy();
  }
}
